import { useEffect, useRef } from 'react'

/**
 * Obsidia's talking-head frame.
 *
 * Canon (docs/02-voices.md) presents her as "interviewed on a laptop webcam /
 * waveform / simple avatar" — so this is a webcam feed, not a hologram. The
 * figure is a low-contrast abstract head that mostly sits back; the waveform is
 * the thing that carries her presence, because on the show she is heard more
 * than she is looked at.
 *
 * Deliberately restrained. She is device-native and unimpressed; a glowing
 * sci-fi construct would be the wrong character.
 */

export type ObsidiaState = 'idle' | 'listening' | 'speaking'

interface Props {
  state: ObsidiaState
  className?: string
}

const BG = '#0b0c0e'
const ACCENT = '#7fb2c9'
// Kept as channels so alpha can vary with the speech level below; matches
// --text / --accent in index.css.
const SIGNAL_RGB = '201, 211, 218'
const ACCENT_RGB = '127, 178, 201'

export function ObsidiaFrame({ state, className }: Props) {
  const ref = useRef<HTMLCanvasElement>(null)
  // Held in a ref so a state change doesn't restart the loop and visibly reset
  // the waveform mid-sentence.
  const stateRef = useRef<ObsidiaState>(state)
  stateRef.current = state

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let w = 0
    let h = 0

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = Math.max(1, Math.floor(rect.width))
      h = Math.max(1, Math.floor(rect.height))
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    // Per-bar phase offsets so the waveform doesn't move as one block.
    const phases = Array.from({ length: 96 }, (_, i) => i * 0.7 + Math.random())

    let raf = 0
    let last = 0
    let level = 0

    const draw = (now: number) => {
      raf = requestAnimationFrame(draw)
      if (now - last < 33) return // ~30fps is plenty
      last = now

      const s = stateRef.current
      // Ease toward the target amplitude so transitions don't snap.
      const target = s === 'speaking' ? 1 : s === 'listening' ? 0.22 : 0.06
      level += (target - level) * (reduced ? 0.5 : 0.18)

      ctx.fillStyle = BG
      ctx.fillRect(0, 0, w, h)

      // ── presence: a soft pool of light where a face would be lit ──────────
      const cx = w / 2
      const headY = h * 0.36
      const glow = ctx.createRadialGradient(cx, headY, 0, cx, headY, Math.min(w, h) * 0.55)
      glow.addColorStop(0, `rgba(${ACCENT_RGB}, ${0.10 + level * 0.06})`)
      glow.addColorStop(1, `rgba(${ACCENT_RGB}, 0)`)
      ctx.fillStyle = glow
      ctx.fillRect(0, 0, w, h)

      // ── the simple avatar: head, neck, shoulders — one connected bust ─────
      // Proportioned so it reads as a person sitting slightly back from a
      // laptop camera: head high, shoulders wide and cropped by the frame.
      const r = Math.min(w, h) * 0.115
      const neckTop = headY + r * 0.92
      const shoulderY = headY + r * 2.5
      ctx.strokeStyle = `rgba(${SIGNAL_RGB}, ${0.3 + level * 0.22})`
      ctx.lineWidth = 1.5
      ctx.lineJoin = 'round'

      ctx.beginPath()
      ctx.ellipse(cx, headY, r * 0.8, r, 0, 0, Math.PI * 2)
      ctx.stroke()

      // Neck + shoulders as a single path so there is no floating gap.
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.34, neckTop)
      ctx.lineTo(cx - r * 0.36, shoulderY - r * 0.5)
      // out to the left shoulder, then down and off the bottom of the frame
      ctx.quadraticCurveTo(cx - r * 1.5, shoulderY - r * 0.35, cx - r * 2.4, shoulderY + r * 0.35)
      ctx.lineTo(cx - r * 2.9, h)
      ctx.moveTo(cx + r * 0.34, neckTop)
      ctx.lineTo(cx + r * 0.36, shoulderY - r * 0.5)
      ctx.quadraticCurveTo(cx + r * 1.5, shoulderY - r * 0.35, cx + r * 2.4, shoulderY + r * 0.35)
      ctx.lineTo(cx + r * 2.9, h)
      ctx.stroke()

      // ── waveform ──────────────────────────────────────────────────────────
      // Sits above the chrome strip, with a visible baseline so it reads as an
      // audio meter at rest rather than a dotted rule.
      const baseY = h * 0.8
      const bw = w / phases.length
      ctx.fillStyle = ACCENT

      ctx.globalAlpha = 0.18
      ctx.fillRect(w * 0.06, baseY - 0.5, w * 0.88, 1)

      for (let i = 0; i < phases.length; i++) {
        // Two detuned sines so it reads organic rather than like an equalizer.
        const t = reduced ? 0 : now / 240
        const a = Math.sin(t + phases[i]) * Math.sin(t * 0.37 + phases[i] * 0.5)
        // Taper toward the edges so it sits in the frame instead of running off.
        const edge = Math.sin((i / phases.length) * Math.PI)
        // Floor of 2px keeps a live-looking meter at idle instead of dots.
        const amp = Math.abs(a) * level * edge * h * 0.16 + 2
        ctx.globalAlpha = 0.4 + level * 0.45
        ctx.fillRect(i * bw + bw * 0.2, baseY - amp, Math.max(1, bw * 0.6), amp * 2)
      }
      ctx.globalAlpha = 1

      // ── webcam grain ──────────────────────────────────────────────────────
      if (!reduced) {
        ctx.fillStyle = `rgba(${SIGNAL_RGB}, 0.02)`
        for (let i = 0; i < 40; i++) {
          ctx.fillRect(Math.random() * w, Math.random() * h, 1, 1)
        }
      }
    }

    raf = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  return (
    <canvas
      ref={ref}
      className={className}
      role="img"
      aria-label="Obsidia's video frame: a simple avatar with an audio waveform."
      style={{ display: 'block', width: '100%', height: '100%' }}
    />
  )
}

export default ObsidiaFrame
